import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import * as Draggable from '@givebutter/primitives/draggable';
import * as Sortable from '@givebutter/primitives/sortable';
import { useToast } from '@givebutter/primitives/toast';
import { reorderAuctionCategories } from 'api/campaigns';
import { useDialogRoute } from 'components/dialog-route/DialogRouteContext';
import * as Drawer from 'components/drawer';
import * as Form from 'components/form';
import { AuctionCategoryForm } from 'components/pages/campaign-settings/campaign-auction/auction-categories/AuctionCategoryForm';
import { useAuctionCategories } from 'components/pages/campaign-settings/campaign-auction/auction-categories/useAuctionCategories';
import { useCurrentCampaign } from 'customHooks/useCurrentCampaign';
import { handleDragEnd } from 'helpers/handle-drag-end';
import { campaignQueryKeys } from 'queries/campaign-query-keys';
import { queryClient } from 'queries/queryClient';
import { GivebutterAxiosError } from 'types/error';

interface AuctionCategoriesDrawerProps {
    categoryOrderOnSuccess?: () => void;
}
interface AuctionItemOrderFormValues {
    order: number[];
}

const AuctionCategoriesDrawer = (props: AuctionCategoriesDrawerProps) => {
    const { categoryOrderOnSuccess } = props;
    const toast = useToast();
    const [isEditingOrder, setIsEditingOrder] = useState(false);
    const { goBack } = useDialogRoute();
    const { data: campaign } = useCurrentCampaign();
    const campaignId = campaign?.id!;
    const auctionId = campaign?.auction_id!;
    const { data: categories } = useAuctionCategories({ campaignId, auctionId });
    const methods = Form.useGivebutterForm<AuctionItemOrderFormValues>({
        values: {
            order: categories?.map(cat => cat.id) ?? [],
        },
    });
    const { handleSubmit, watch, setValue, resetFormButton } = methods;
    const order = watch('order');
    const { mutate: submit } = useMutation({
        mutationFn: () => {
            return reorderAuctionCategories(campaignId, auctionId, order);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: campaignQueryKeys.auctionCategories(campaignId, auctionId),
            });
            toast.success('Updated category order');
            categoryOrderOnSuccess?.();
            goBack?.();
        },
        onError: (error: GivebutterAxiosError) => {
            resetFormButton();
            toast.error(error.response?.data.message ?? 'Unknown error updating category order');
        },
    });

    return (
        <Form.GivebutterFormProvider {...methods}>
            <form className="contents" onSubmit={handleSubmit(() => submit())}>
                <Drawer.Header>
                    <Drawer.Title>Manage categories</Drawer.Title>
                    <Drawer.Close />
                </Drawer.Header>
                <Drawer.Body>
                    <div className="flex flex-col gap-4">
                        <div>
                            <p className="font-medium text-gray-800">
                                Categorize your auction items
                            </p>
                            <p className="text-sm text-gray-600">
                                You can organize your items based on categories. This is the order
                                of how your categories will show up on the auction page. Hover over
                                a category to reorder.
                            </p>
                        </div>

                        {!!categories?.length && (
                            <>
                                <Draggable.Root
                                    onDragEnd={event => {
                                        setIsEditingOrder(true);
                                        setValue('order', handleDragEnd(event, order));
                                    }}
                                >
                                    <Sortable.Root items={order}>
                                        <div className="flex flex-col gap-1">
                                            {order.map(catId => {
                                                const cat = categories?.find(c => c.id === catId);

                                                if (!cat) {
                                                    return null;
                                                }

                                                return (
                                                    <AuctionCategoryForm
                                                        key={cat.id}
                                                        category={cat}
                                                    />
                                                );
                                            })}
                                        </div>
                                    </Sortable.Root>
                                </Draggable.Root>
                                <hr className="text-gray-100" />
                            </>
                        )}

                        <AuctionCategoryForm />
                    </div>
                </Drawer.Body>
                <Drawer.Footer>
                    <div
                        className={
                            'flex w-full flex-wrap-reverse items-center justify-between gap-2'
                        }
                    >
                        <Drawer.Cancel asChild={true}>
                            <Button variant={'transparent'}>Cancel</Button>
                        </Drawer.Cancel>
                        {isEditingOrder && (
                            <Form.SubmitButton>Save category order</Form.SubmitButton>
                        )}
                    </div>
                </Drawer.Footer>
            </form>
        </Form.GivebutterFormProvider>
    );
};

export { AuctionCategoriesDrawer };
